// a class to handler communication between an iframe and the parent window

import {
  BaseCommunicator,
  CommunicationDataTransfer,
  CommunicationDataType,
  GameDataDefinition,
  isCommunicationDataTransfer,
} from '@toilet-paper-games/core';

import { controllerAppModel } from '@/controller/App';

import { NetworkDataType } from '../../common/models/NetworkDataTransfers';
import { config } from '../../common/config';
import { ControllerGamePlayerViewModel } from '../components/ControllerGamePlayer';
import { ignoredCommunicationDataDebugMessageTypes } from '@/common/constants';

export class CommunicationHandlerAppController<
  T extends GameDataDefinition,
> extends BaseCommunicator<T> {
  messenger: (data: unknown) => void = () => {
    throw new Error('Messenger not set');
  };

  /** setupMessenger must be used to setup messenger */
  constructor(private controllerGamePlayerViewModel: ControllerGamePlayerViewModel) {
    super();
  }

  setupMessenger(messenger: (data: unknown) => void) {
    this.messenger = messenger;
  }

  sendToFrame(data: CommunicationDataTransfer<T>) {
    if (!isCommunicationDataTransfer(data)) {
      throw new Error('Invalid communication data transfer');
    }

    if (config.debugMode) {
      if (!ignoredCommunicationDataDebugMessageTypes.includes(data.type)) {
        console.info('[TPG] Sending to frame', data);
      }
    }

    this.messenger(data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messageHandler(message: unknown) {
    super.messageHandler(message);

    if (!isCommunicationDataTransfer(message)) {
      throw new Error('Invalid communication data transfer');
    }

    if (config.debugMode) {
      if (!ignoredCommunicationDataDebugMessageTypes.includes(message.type)) {
        console.info('[TPG] Receiving from frame', message);
      }
    }

    if (message.type === CommunicationDataType.DEBUG) {
      controllerAppModel.networkHandler.hostConnection?.send({
        type: NetworkDataType.DEBUG,
        data: message.data,
      });
    }

    // TODO: Do this first, this needs whole class needs to have a version for controllers. then this part right here will send right to the host connection, no .find needed.
    if (message.type === CommunicationDataType.PONG_CONTROLLER) {
      controllerAppModel.networkHandler.hostConnection?.send({
        type: NetworkDataType.PONG_C2H,
        data: {
          controllerTime: message.data.controllerTime,
          id: message.data.id,
          userId: message.data.playerId,
        },
      });
    }

    if (message.type === CommunicationDataType.GAME_ACTION_CONTROLLER) {
      controllerAppModel.networkHandler.hostConnection?.send({
        type: NetworkDataType.GAME_ACTION,
        data: message.data.payload,
      });
    }

    if (message.type === CommunicationDataType.READY_STATUS_CONTROLLER) {
      controllerAppModel.networkHandler.hostConnection?.send({
        type: NetworkDataType.GAME_READY_STATUS_CONTROLLER_C2H,
        data: message.data,
      });
    }

    if (message.type === CommunicationDataType.END_GAME_CONTROLLER) {
      controllerAppModel.networkHandler.hostConnection?.send({
        type: NetworkDataType.END_GAME,
        data: message.data,
      });
    }

    if (message.type === CommunicationDataType.RELOAD_GAME_CONTROLLER) {
      controllerAppModel.networkHandler.hostConnection?.send({
        type: NetworkDataType.REFRESH,
        data: message.data,
      });
    }
  }

  addReadyListener(listener: () => void): { destroy: () => void } {
    const readyListener = (message: CommunicationDataTransfer<T>) => {
      if (
        message.type === CommunicationDataType.READY_STATUS_CONTROLLER &&
        message.data.ready
      ) {
        listener();
      }
    };

    const destructor = this.addAppMessageListener(readyListener);

    return destructor;
  }

  waitForReady(): Promise<void> {
    return new Promise((resolve) => {
      const destructor = this.addReadyListener(() => {
        resolve();
        destructor.destroy();
      });
    });
  }
}
